import type { ApproverSource } from '@clockwise/clients/approval-policies';
import { useQuery } from '@tanstack/react-query';
import { ShieldCheck, User as UserIcon, Users, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { systemRolesApi, usersApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

/**
 * Render a single approver-source as a chip. Split into three small
 * components (one per source kind) so each only mounts the queries it
 * actually needs — the previous `useSourceDisplay` hook had to mount
 * BOTH `useQuery` calls on every chip regardless of kind, because the
 * rules of hooks require unconditional invocation. Splitting at the
 * component level lets React mount the right hooks for the right kind.
 *
 * The static labels for `kind: 'type'` sources are co-located here so
 * the picker and the chip can't disagree on display strings.
 */

export const APPROVER_TYPE_LABELS: Record<
  Extract<ApproverSource, { kind: 'type' }>['type'],
  string
> = {
  users_manager: "User's manager",
  customer_team_lead: 'Customer team lead',
};

interface ChipProps {
  source: ApproverSource;
  onRemove?: () => void;
}

export function ApproverSourceBadge({ source, onRemove }: ChipProps): React.ReactElement {
  if (source.kind === 'type') return <TypeChip source={source} onRemove={onRemove} />;
  if (source.kind === 'user') return <UserChip userId={source.userId} onRemove={onRemove} />;
  return <RoleChip roleId={source.roleId} onRemove={onRemove} />;
}

function ChipShell({
  icon,
  label,
  onRemove,
}: {
  icon: React.ReactNode;
  label: string;
  onRemove?: () => void;
}): React.ReactElement {
  return (
    <Badge variant="secondary" className="gap-1.5 pr-1">
      {icon}
      <span>{label}</span>
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="ml-1 rounded-sm hover:bg-muted"
          aria-label="Remove approver"
        >
          <X className="size-3" />
        </button>
      )}
    </Badge>
  );
}

function TypeChip({
  source,
  onRemove,
}: {
  source: Extract<ApproverSource, { kind: 'type' }>;
  onRemove?: () => void;
}) {
  return (
    <ChipShell
      icon={<Users className="size-3 text-muted-foreground" />}
      label={APPROVER_TYPE_LABELS[source.type]}
      onRemove={onRemove}
    />
  );
}

function UserChip({ userId, onRemove }: { userId: string; onRemove?: () => void }) {
  const userQuery = useQuery({
    queryKey: queryKeys.users.detail(userId),
    queryFn: () => usersApi.get(userId),
    staleTime: 5 * 60_000,
  });
  const user = userQuery.data?.data;
  const label = user
    ? `${user.firstName} ${user.lastName}`.trim() || user.email
    : userQuery.isLoading
      ? '…'
      : 'Unknown user';
  return (
    <ChipShell
      icon={<UserIcon className="size-3 text-muted-foreground" />}
      label={label}
      onRemove={onRemove}
    />
  );
}

function RoleChip({ roleId, onRemove }: { roleId: string; onRemove?: () => void }) {
  const roleQuery = useQuery({
    queryKey: queryKeys.systemRoles.detail(roleId),
    queryFn: () => systemRolesApi.get(roleId),
    staleTime: 5 * 60_000,
  });
  const role = roleQuery.data?.data;
  const label = role?.name ?? (roleQuery.isLoading ? '…' : 'Unknown role');
  return (
    <ChipShell
      icon={<ShieldCheck className="size-3 text-muted-foreground" />}
      label={`Role: ${label}`}
      onRemove={onRemove}
    />
  );
}
